import { Button } from 'primereact/button';
import { Divider } from 'primereact/divider';
import { classNames } from 'primereact/utils';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { DeclaredUnit } from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import { EmissionSymbol } from '../../../../../../../../../../../shared/interfaces/IFootprint';
import { IWasteManagement, SprueTreatmentSelection } from '../../../PcfWizard.type';
import styles from '../CalculationDetails.module.css';

type ExpandableInjectionMoldingWasteTreatmentDetailsProps = {
  wasteManagement?: IWasteManagement;
};

const ExpandableInjectionMoldingWasteTreatmentDetails = ({
  wasteManagement,
}: ExpandableInjectionMoldingWasteTreatmentDetailsProps) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const sprueTreatment = wasteManagement?.sprue_waste_treatment;
  const scrapTreatment = wasteManagement?.scrap_waste_treatment;

  const sprueContent = (
    <div className='flex flex-column w-full gap-3'>
      <div className={classNames(styles['waste-treatment-type'], 'py-1 px-2 w-fit font-medium')}>
        {t('calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.sprue.title')}
      </div>

      <div className='grid w-full grid-nogutter'>
        {/* Sprue Weight per Shot */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.sprue.sprueWeightPerShot',
            )}
          </span>
          <div className='flex flex-row gap-1 text-lg'>
            <span>{wasteManagement?.sprue_weight}</span>
            <span>kg</span>
          </div>
        </div>

        {/* Sprue Treatment */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.sprue.sprueTreatment',
            )}
          </span>
          <span className='text-lg'>
            {wasteManagement?.sprue_treatment_type ===
            SprueTreatmentSelection.REUSED_IN_SAME_MACHINE
              ? t(
                  'footprintDetailsPage.sections.calculatePcfSection.injectionMoldingSection.wasteManagementSection.inputs.sprueTreatmentTypeOptions.reusedInSameMachine',
                )
              : sprueTreatment?.name}
          </span>
        </div>
      </div>

      {/* Show the rest of Sprue details if the type is DISCARDED */}
      {wasteManagement?.sprue_treatment_type === SprueTreatmentSelection.DISCARDED && (
        <div className='grid w-full grid-nogutter'>
          {/* Source */}
          <div className='col-6 flex flex-column gap-1 pr-2'>
            <span className='text-gray-700 font-medium'>
              {t(
                'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.sprue.source',
              )}
            </span>
            <div className='text-lg'>
              <span>{sprueTreatment?.source}</span>
            </div>
          </div>

          {/* Emission Factor */}
          <div className='col-6 flex flex-column gap-1 pl-2'>
            <span className='text-gray-700 font-medium'>
              {t(
                'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.sprue.emissionFactor',
              )}
            </span>

            <EmissionValueWithSuffix
              emissionValue={sprueTreatment?.pcfExcludingBiogenic}
              declaredUnit={DeclaredUnit.kilogram}
              unitaryProductAmount={1}
              emissionSymbol={EmissionSymbol.KG_CO2E}
              abbreviateUnit={true}
              emissionValueDecimals={6}
              numericalValueTextClassName='text-lg'
              suffixTextClassName='text-lg'
            />
          </div>
        </div>
      )}

      {/* Comment */}
      {sprueTreatment?.comment && (
        <div className='flex flex-column gap-1 w-full'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.sprue.comment',
            )}
          </span>
          <span>{sprueTreatment?.comment}</span>
        </div>
      )}
    </div>
  );

  const scrapContent = (
    <div className='flex flex-column w-full gap-3'>
      <div className={classNames(styles['waste-treatment-type'], 'py-1 px-2 w-fit font-medium')}>
        {t('calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.title')}
      </div>

      <div className='grid w-full grid-nogutter'>
        {/* Scrap Rate */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.scrapRate',
            )}
          </span>
          <span className='text-lg'>{wasteManagement?.scrap_rate} %</span>
        </div>

        {/* Scrap Treatment */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.scrapTreatment',
            )}
          </span>
          <span className='text-lg'>{scrapTreatment?.name}</span>
        </div>
      </div>

      <div className='grid w-full grid-nogutter'>
        {/* Source */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.source',
            )}
          </span>
          <span className='text-lg'>{scrapTreatment?.source}</span>
        </div>

        {/* Emission Factor */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.emissionFactor',
            )}
          </span>

          <EmissionValueWithSuffix
            emissionValue={scrapTreatment?.pcfExcludingBiogenic}
            declaredUnit={DeclaredUnit.kilogram}
            unitaryProductAmount={1}
            emissionSymbol={EmissionSymbol.KG_CO2E}
            abbreviateUnit={true}
            emissionValueDecimals={6}
            numericalValueTextClassName='text-lg'
            suffixTextClassName='text-lg'
          />
        </div>
      </div>

      {/* Comment */}
      {scrapTreatment?.comment && (
        <div className='flex flex-column gap-1 w-full'>
          <span className='text-gray-700 font-medium'>
            {t(
              'calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.scrap.comment',
            )}
          </span>
          <span>{scrapTreatment?.comment}</span>
        </div>
      )}
    </div>
  );

  const expandedContent = (
    <div className='flex flex-column gap-4'>
      {/* Sprue Details */}
      {sprueContent}

      {/* Scrap Details */}
      {scrapContent}

      <Divider className='p-2 m-0 opacity-60' />
    </div>
  );

  const onExpandClick = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className='flex flex-column gap-3 w-full'>
      {/* Title & Expand Button */}
      <div className='flex flex-row gap-2 align-items-center'>
        <span className='text-lg font-medium'>
          {t('calculationDetailsPage.sections.injectionMoldingProcess.wasteTreatment.title')}
        </span>

        <Button
          icon={isExpanded ? 'pi pi-angle-up' : 'pi pi-angle-down'}
          onClick={onExpandClick}
          text
          className='p-2 m-0 -my-1 text-black-alpha-90 w-min hover:bg-black-alpha-20 border-round-lg bg-black-alpha-10 opacity-60'
          pt={{ icon: { className: 'font-bold' } }}
        />
      </div>

      {/* Expanded Content */}
      {isExpanded && expandedContent}

      {/* Emissions */}
      <div className='flex flex-column gap-1'>
        <span className='text-gray-700 font-medium'>
          {t('calculationDetailsPage.inputMaterial.emissions')}
        </span>

        <EmissionValueWithSuffix
          emissionValue={wasteManagement?.emissions}
          declaredUnit={DeclaredUnit.piece}
          unitaryProductAmount={1}
          emissionSymbol={EmissionSymbol.KG_CO2E}
          abbreviateUnit={true}
          emissionValueDecimals={6}
          numericalValueTextClassName='text-lg'
          suffixTextClassName='text-lg'
        />
      </div>
    </div>
  );
};

export default ExpandableInjectionMoldingWasteTreatmentDetails;
